'use client';

import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useState,
} from 'react';

import type { UserData, ValidityType } from '@/lib/types';

type SetUserDataAction =
  | UserData
  | null
  | ((prevState: UserData | null) => UserData | null)
  | ((prevState: UserData) => UserData);

interface DataContextType {
  data: UserData | null;
  validity: ValidityType;
  setData: Dispatch<SetUserDataAction>;
  setValidity: Dispatch<SetStateAction<ValidityType>>;
}

export const DataContext = createContext<DataContextType | undefined>(undefined);

export const emptyValidityState: ValidityType = {
  contactFormValid: false,
  secondTenantFormValid: false,
  paymentFormValid: false,
  summaryFormValid: false,
};

export function DataProvider({ children }: { children: ReactNode }) {
  const [data, setDataState] = useState<UserData | null>(null);
  const [validity, setValidity] = useState<ValidityType>(emptyValidityState);

  const setData: Dispatch<SetUserDataAction> = useCallback((value) => {
    if (typeof value === 'function') {
      setDataState((prevState) =>
        (value as (prevState: UserData | null) => UserData | null)(prevState)
      );
      return;
    }

    setDataState(value);
  }, []);

  return (
    <DataContext.Provider value={{ data, validity, setData, setValidity }}>
      {children}
    </DataContext.Provider>
  );
}

export function useCid() {
  const context = useContext(DataContext);
  return context?.data?.immutableData?.hash || null;
}

export function useData() {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
}
